import { addDays, formatDayShort, isoDate, parseIsoDate, pluralAthletes } from './schedule'
import type {
  ClubEvent,
  EventKind,
  EventMoment,
  EventParticipant,
  ParticipantPayment,
  ParticipationStatus,
} from './types'

/**
 * Соревнования, сборы и аттестации в новом интерфейсе (events.md): подписи,
 * даты и раскладка по дням. Подписи без рода - пол по имени не угадываем.
 */

export const EVENT_KINDS: EventKind[] = ['competition', 'training_camp', 'exam', 'seminar', 'other']

export const EVENT_KIND_LABEL: Record<EventKind, string> = {
  competition: 'Соревнование',
  training_camp: 'Сборы',
  exam: 'Аттестация',
  seminar: 'Семинар',
  other: 'Другое',
}

/** Для фильтра списка: «Соревнования», «Аттестации». */
export const EVENT_KIND_PLURAL: Record<EventKind, string> = {
  competition: 'Соревнования',
  training_camp: 'Сборы',
  exam: 'Аттестации',
  seminar: 'Семинары',
  other: 'Другое',
}

export const PARTICIPATION_LABEL: Record<ParticipationStatus, string> = {
  registered: 'В заявке',
  confirmed: 'Идёт',
  cancelled: 'Не идёт',
  participated: 'Участие засчитано',
  no_show: 'Неявка',
}

export const PARTICIPATION_ORDER: ParticipationStatus[] = ['registered', 'confirmed', 'cancelled', 'participated', 'no_show']

export const PAYMENT_LABEL: Record<ParticipantPayment, string> = {
  not_paid: 'Нет отметки',
  paid: 'Оплачено',
  exempt: 'Без оплаты',
}

export const PAYMENT_ORDER: ParticipantPayment[] = ['not_paid', 'paid', 'exempt']

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * Дата из API в часах устройства. Событие без времени прежний интерфейс и
 * этот сохраняют как дату (`2026-10-18` - полночь UTC): такое значение - «весь
 * день», дата берётся по UTC, иначе в Москве оно выглядело бы как «03:00».
 */
export function fromServerDate(raw: string | null | undefined): EventMoment | null {
  if (!raw) return null
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
    return { date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`, time: '' }
  }
  return { date: isoDate(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }
}

/**
 * Дата для API. Без времени - только дата (весь день). Со временем - со
 * смещением часового пояса устройства на эту дату. Если время устройства
 * совпадает с полночью UTC (03:00 в Москве), добавляется секунда, иначе
 * событие читалось бы как «весь день».
 */
export function toServerDate(m: EventMoment): string {
  if (!m.time) return m.date
  const [y, mo, d] = m.date.split('-').map(Number)
  const [h, mi] = m.time.split(':').map(Number)
  const local = new Date(y, mo - 1, d, h, mi, 0)
  const offset = -local.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const abs = Math.abs(offset)
  const seconds = local.getUTCHours() === 0 && local.getUTCMinutes() === 0 ? '01' : '00'
  return `${m.date}T${pad(h)}:${pad(mi)}:${seconds}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

/** Последний день события (YYYY-MM-DD). */
export function eventLastDay(e: Pick<ClubEvent, 'start' | 'end'>): string {
  return e.end && e.end.date > e.start.date ? e.end.date : e.start.date
}

/** Событие идёт в этот день (многодневное - в любой из дней). */
export function isOnDay(e: Pick<ClubEvent, 'start' | 'end'>, day: string): boolean {
  return e.start.date <= day && day <= eventLastDay(e)
}

/** Прошло: последний день раньше сегодня. */
export function isPastEvent(e: Pick<ClubEvent, 'start' | 'end'>, today: string): boolean {
  return eventLastDay(e) < today
}

/**
 * До начала - тренер собирает заявку («Идёт» / «Не идёт»), с дня начала -
 * отмечает, кто на месте («Есть» / «Нет»), как на тренировке.
 */
export function eventPhase(e: Pick<ClubEvent, 'start'>, today: string): 'before' | 'started' {
  return e.start.date <= today ? 'started' : 'before'
}

export function quickStatuses(phase: 'before' | 'started'): [ParticipationStatus, string][] {
  return phase === 'before'
    ? [
        ['confirmed', 'Идёт'],
        ['cancelled', 'Не идёт'],
      ]
    : [
        ['participated', 'Есть'],
        ['no_show', 'Нет'],
      ]
}

const startKey = (e: ClubEvent) => `${e.start.date} ${e.start.time || '00:00'}`

/** Ближайшие (идущие и будущие) по возрастанию, прошедшие - свежие первыми. */
export function splitEvents(list: ClubEvent[], today: string): { upcoming: ClubEvent[]; past: ClubEvent[] } {
  const upcoming = list.filter((e) => !isPastEvent(e, today)).sort((a, b) => startKey(a).localeCompare(startKey(b)))
  const past = list.filter((e) => isPastEvent(e, today)).sort((a, b) => startKey(b).localeCompare(startKey(a)))
  return { upcoming, past }
}

/** События, которые идут в этот день, по началу. */
export function eventsOnDay(list: ClubEvent[], day: string): ClubEvent[] {
  return list.filter((e) => isOnDay(e, day)).sort((a, b) => startKey(a).localeCompare(startKey(b)))
}

/** Для «Сегодня»: идёт сегодня или начнётся в ближайшие days дней. */
export function eventsSoon(list: ClubEvent[], today: Date, days = 7): ClubEvent[] {
  const from = isoDate(today)
  const to = isoDate(addDays(today, days))
  return splitEvents(list, from).upcoming.filter((e) => e.start.date <= to)
}

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

/**
 * Когда: «18 октября, 10:00», «18 октября», «1 - 10 июля», «28 июня - 3 июля».
 * Год - если не текущий (thisYear).
 */
export function formatEventWhen(e: Pick<ClubEvent, 'start' | 'end'>, thisYear: number, withTime = true): string {
  const s = parseIsoDate(e.start.date)
  if (!s) return e.start.date
  const last = parseIsoDate(eventLastDay(e)) ?? s
  const year = (d: Date) => (d.getFullYear() !== thisYear ? ` ${d.getFullYear()}` : '')
  if (isoDate(last) === isoDate(s)) {
    const time = withTime && e.start.time ? `, ${e.start.time}` : ''
    const endTime = withTime && e.start.time && e.end?.time && e.end.date === e.start.date ? ` - ${e.end.time}` : ''
    return `${formatDayShort(s)}${year(s)}${time}${endTime}`
  }
  if (s.getMonth() === last.getMonth() && s.getFullYear() === last.getFullYear()) {
    return `${s.getDate()} - ${last.getDate()} ${MONTHS_GEN[last.getMonth()]}${year(last)}`
  }
  return `${formatDayShort(s)}${s.getFullYear() !== last.getFullYear() ? year(s) : ''} - ${formatDayShort(last)}${year(last)}`
}

/** «сегодня», «завтра», «через 3 дня», «идёт сейчас», «через 2 недели». */
export function relativeStart(e: Pick<ClubEvent, 'start' | 'end'>, today: Date): string {
  const t = isoDate(today)
  if (e.start.date < t && isOnDay(e, t)) return 'идёт сейчас'
  const start = parseIsoDate(e.start.date)
  if (!start) return ''
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const days = Math.round((start.getTime() - base.getTime()) / 86_400_000)
  if (days < 0) return 'прошло'
  if (days === 0) return 'сегодня'
  if (days === 1) return 'завтра'
  return `через ${plural(days, 'день', 'дня', 'дней')}`
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

/** «Будет добавлен 1 спортсмен», «Будут добавлены 2 спортсмена», «Будут добавлены 5 спортсменов». */
export function addedAthletes(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  const one = mod10 === 1 && mod100 !== 11
  return `${one ? 'Будет добавлен' : 'Будут добавлены'} ${pluralAthletes(n)}`
}

/** «12 участников» */
export function pluralParticipants(n: number): string {
  return plural(n, 'участник', 'участника', 'участников')
}

/** «1 500 ₽», «1 500,50 ₽». Не число - как есть. */
export function formatFee(fee: string): string {
  const n = Number(fee.replace(',', '.'))
  if (!Number.isFinite(n)) return fee
  const [int, frac] = n.toFixed(2).split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${grouped}${frac === '00' ? '' : `,${frac}`} ₽`
}

/**
 * Результат участника из API: JSON. ИИ пишет его строкой («1 место»), прежние
 * данные могут быть объектом или числом - показываем как текст.
 */
export function resultText(raw: unknown): string {
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'string') return raw.trim()
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw)
  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(', ')
  }
  return ''
}

/** Итог по участникам для шапки списка. */
export function participantSummary(list: EventParticipant[], phase: 'before' | 'started'): string {
  const count = (s: ParticipationStatus) => list.filter((p) => p.status === s).length
  if (phase === 'before') {
    const parts = [`идут: ${count('confirmed')}`]
    if (count('registered')) parts.push(`в заявке: ${count('registered')}`)
    if (count('cancelled')) parts.push(`не идут: ${count('cancelled')}`)
    return parts.join(', ')
  }
  const parts = [`участие засчитано: ${count('participated')}`]
  if (count('no_show')) parts.push(`неявка: ${count('no_show')}`)
  const open = count('registered') + count('confirmed')
  if (open) parts.push(`не отмечено: ${open}`)
  if (count('cancelled')) parts.push(`не идут: ${count('cancelled')}`)
  return parts.join(', ')
}
